import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  type ErrorLogEntry,
  type MonitoringError,
  type MonitoringMachine,
} from "../api.js";
import { useToken } from "../auth.js";
import { Badge } from "../components/Badge.js";

type MachineFilter = "all" | "online" | "offline";

interface Summary {
  offlineThresholdMinutes: number;
  machinesTotal: number;
  machinesActive: number;
  machinesRevoked: number;
  machinesOnline: number;
  machinesOffline: number;
  applyOkLast24h: number;
  applyFailedLast24h: number;
  errorsLast24h: number;
  runtimeErrorsLast24h: number;
}

// Fixed refresh cadence — matches the local UI. Overkill for a monitoring dashboard where
// data changes every 5 min, but keeps the operator's picture current without a manual reload.
const REFRESH_MS = 30_000;

export function Monitoring() {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [machines, setMachines] = useState<MonitoringMachine[]>([]);
  const [errors, setErrors] = useState<MonitoringError[]>([]);
  const [runtimeErrors, setRuntimeErrors] = useState<ErrorLogEntry[]>([]);
  const [filter, setFilter] = useState<MachineFilter>("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const [sumRes, machinesRes, errRes, runtimeRes] = await Promise.all([
        api.monitoringSummary(token),
        api.monitoringMachines(token, { filter }),
        api.monitoringErrors(token, { limit: 50 }),
        api.listErrors(token, { level: ["error"], sinceMinutes: 60 * 24, limit: 10 }),
      ]);
      setSummary({
        offlineThresholdMinutes: sumRes.offlineThresholdMinutes,
        ...sumRes.summary,
      });
      setMachines(machinesRes.machines);
      setErrors(errRes.errors);
      setRuntimeErrors(runtimeRes.errors);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div>
      <header className="page-header">
        <h1><Trans>Monitoring</Trans></h1>
        <button className="ghost" onClick={() => void load()}>
          <Trans>Refresh</Trans>
        </button>
      </header>

      {loadError && <div className="banner err">{loadError}</div>}

      <section className="kpis">
        <Kpi label={t`Machines`} value={summary?.machinesTotal ?? "—"} />
        <Kpi label={t`Online`} value={summary?.machinesOnline ?? "—"} tone="ok" />
        <Kpi label={t`Offline`} value={summary?.machinesOffline ?? "—"} tone="warn" />
        <Kpi label={t`Revoked`} value={summary?.machinesRevoked ?? "—"} tone="muted" />
        <Kpi
          label={t`Apply ok (24h)`}
          value={summary?.applyOkLast24h ?? "—"}
          tone="ok"
        />
        <Kpi
          label={t`Apply failed (24h)`}
          value={summary?.applyFailedLast24h ?? "—"}
          tone={summary?.applyFailedLast24h ? "err" : "muted"}
        />
        <Kpi
          label={t`Errors (24h)`}
          value={summary?.errorsLast24h ?? "—"}
          tone={summary?.errorsLast24h ? "err" : "muted"}
        />
        <Kpi
          label={t`Runtime errors (24h)`}
          value={summary?.runtimeErrorsLast24h ?? "—"}
          tone={summary?.runtimeErrorsLast24h ? "err" : "muted"}
        />
      </section>
      {summary && (
        <p className="muted">
          <Trans>Offline threshold: {summary.offlineThresholdMinutes} min without heartbeat.</Trans>
        </p>
      )}

      <section>
        <div className="section-header">
          <h2><Trans>Machines</Trans></h2>
          <div className="filters">
            {(["all", "online", "offline"] as const).map((f) => (
              <button
                key={f}
                className={f === filter ? "chip active" : "chip"}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? t`all` : f === "online" ? t`online` : t`offline`}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p className="muted"><Trans>Loading…</Trans></p>
        ) : machines.length === 0 ? (
          <p className="muted"><Trans>No machines match this filter.</Trans></p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th><Trans>Customer</Trans></th>
                  <th><Trans>Machine</Trans></th>
                  <th><Trans>Status</Trans></th>
                  <th><Trans>Agent</Trans></th>
                  <th><Trans>Sage SQL</Trans></th>
                  <th><Trans>Sage Web API</Trans></th>
                  <th><Trans>Config</Trans></th>
                  <th><Trans>Last error</Trans></th>
                  <th><Trans>Last heartbeat</Trans></th>
                </tr>
              </thead>
              <tbody>
                {machines.map((m) => (
                  <tr key={m.machineId}>
                    <td>
                      <Link to={`/customers/${m.customerId}`}>{m.customerName}</Link>
                    </td>
                    <td>
                      {m.machineName ?? "—"}
                      <div className="muted mono short-id">{m.machineId.slice(0, 8)}…</div>
                    </td>
                    <td>
                      <Badge tone={m.online ? "ok" : "warn"}>
                        {m.online ? t`online` : t`offline`}
                      </Badge>
                      {m.machineStatus !== "active" && (
                        <Badge tone="unknown">{m.machineStatus}</Badge>
                      )}
                    </td>
                    <td>{m.agentVersion ?? "—"}</td>
                    <td><StatusBadge status={m.sageSqlStatus} /></td>
                    <td><StatusBadge status={m.sageWebApiStatus} /></td>
                    <td>
                      {m.currentConfigVersion !== null ? `v${m.currentConfigVersion}` : "—"}
                      {m.lastApplyStatus === "failed" && (
                        <div className="muted err-text"><Trans>failed</Trans></div>
                      )}
                    </td>
                    <td className="mono">{m.lastErrorCode ?? "—"}</td>
                    <td><HeartbeatAge seconds={m.lastHeartbeatAgeSeconds} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="section-header">
          <h2><Trans>Recent errors (24h)</Trans></h2>
        </div>
        {errors.length === 0 ? (
          <p className="muted"><Trans>No errors reported in the last 24h.</Trans></p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th><Trans>When</Trans></th>
                  <th><Trans>Source</Trans></th>
                  <th><Trans>Customer</Trans></th>
                  <th><Trans>Machine</Trans></th>
                  <th><Trans>Detail</Trans></th>
                </tr>
              </thead>
              <tbody>
                {errors.map((e) => (
                  <tr key={`${e.kind}:${e.id}`}>
                    <td>{new Date(e.receivedAt).toLocaleString(i18n.locale)}</td>
                    <td>
                      <Badge tone={e.kind === "apply" ? "err" : "warn"}>
                        {e.kind === "apply" ? t`apply ${e.status}` : t`heartbeat`}
                      </Badge>
                    </td>
                    <td>
                      <Link to={`/customers/${e.customerId}`}>{e.customerName}</Link>
                    </td>
                    <td>{e.machineName ?? e.machineId.slice(0, 8) + "…"}</td>
                    {e.kind === "heartbeat" ? (
                      <td className="mono">{e.lastErrorCode}</td>
                    ) : (
                      <td>
                        <div className="mono" title={e.error ?? undefined}>
                          {truncate(e.error ?? t`(no message)`, 120)}
                        </div>
                        <div className="muted short-id">
                          v{e.configVersion ?? "—"} · {e.source ?? "—"}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="section-header">
          <h2><Trans>Runtime errors (24h)</Trans></h2>
          <Link to="/errors" className="ghost"><Trans>View all →</Trans></Link>
        </div>
        {runtimeErrors.length === 0 ? (
          <p className="muted"><Trans>No runtime errors in the last 24h.</Trans></p>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th><Trans>When</Trans></th>
                  <th><Trans>Origin</Trans></th>
                  <th><Trans>Source</Trans></th>
                  <th><Trans>Message</Trans></th>
                </tr>
              </thead>
              <tbody>
                {runtimeErrors.map((e) => (
                  <tr key={e.id}>
                    <td>{new Date(e.loggedAt).toLocaleString(i18n.locale)}</td>
                    <td><Badge tone="warn">{e.origin}</Badge></td>
                    <td className="mono short-id">{e.source ?? "—"}</td>
                    <td title={e.message}>{truncate(e.message, 160)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}


function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: "ok" | "warn" | "err" | "muted";
}) {
  return (
    <div className={`kpi${tone ? " kpi-" + tone : ""}`}>
      <div className="kpi-value">{value}</div>
      <div className="kpi-label">{label}</div>
    </div>
  );
}

function StatusBadge({ status }: { status: string | null }) {
  const { t } = useLingui();
  if (status === "ok") return <Badge tone="ok">{t`ok`}</Badge>;
  if (status === "error") return <Badge tone="err">{t`error`}</Badge>;
  if (status === "unknown" || status === null) return <span className="muted">—</span>;
  return <span>{status}</span>;
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function HeartbeatAge({ seconds }: { seconds: number | null }) {
  const { t } = useLingui();
  if (seconds === null || !Number.isFinite(seconds)) return <>{t`never`}</>;
  if (seconds < 60) return <>{t`${seconds}s ago`}</>;
  if (seconds < 3600) return <>{t`${Math.floor(seconds / 60)}m ago`}</>;
  if (seconds < 86400) return <>{t`${Math.floor(seconds / 3600)}h ago`}</>;
  return <>{t`${Math.floor(seconds / 86400)}d ago`}</>;
}
